import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpRight, Banknote, Plus, Send, X } from 'lucide-react';
import {
  ApiClientError, DataTable, EmptyState, Money, Spinner, enumLabel, relativeTime, type Column,
} from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Payouts
 *
 *  Money leaving the account — to a vendor, a seller, a refund beneficiary, a
 *  salary. Everything created through the API appears here alongside anything
 *  created from this screen; there is one list because there is one ledger.
 *
 *  A payout debits the available balance the moment it is queued, not when the
 *  bank confirms. That is deliberate: the alternative lets a merchant queue the
 *  same balance twice while the first transfer is still in flight.
 * ========================================================================== */

interface Payout {
  id: string;
  status: 'QUEUED' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'REVERSED' | 'ON_HOLD';
  purpose: string;
  rail: string;
  amount: { amount: string; currency: string; formatted: string };
  fee: { amount: string; currency: string; formatted: string };
  total_debited: { amount: string; currency: string; formatted: string };
  beneficiary: { name: string; last4: string | null; ifsc: string | null; vpa: string | null } | null;
  utr: string | null;
  failure_reason: string | null;
  created_at: string;
  processed_at: string | null;
}

// Mirrors the `purpose` enum on POST /v1/payouts. Kept in the same order the
// API declares it so the two are easy to diff by eye.
const PURPOSES = ['VENDOR_PAYMENT', 'BULK_PAYOUT', 'REFUND_FUNDING', 'SALARY', 'SETTLEMENT', 'OTHER'] as const;

function StatusPill({ status }: { status: Payout['status'] }) {
  const tone =
    status === 'SUCCESS' ? 'badge-success'
    : status === 'FAILED' || status === 'REVERSED' ? 'badge-danger'
    : status === 'ON_HOLD' ? 'badge-warning'
    : 'badge-info';
  return <span className={`badge ${tone}`}>{enumLabel(status)}</span>;
}

export function Payouts() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payouts = useQuery({
    queryKey: ['payouts'],
    queryFn: () => api.get<{ data: Payout[] }>('/payouts?limit=100'),
    refetchInterval: 30_000,
  });

  const rows = payouts.data?.data ?? [];
  const inFlight = rows.filter((p) => p.status === 'QUEUED' || p.status === 'PROCESSING').length;

  const columns: Column<Payout>[] = [
    {
      key: 'ref',
      header: 'Payout',
      render: (p) => (
        <div className="min-w-0">
          <p className="numeral text-xs text-content">{p.id}</p>
          <p className="mt-1 text-2xs text-content-faint">
            {enumLabel(p.purpose)} · {p.rail}
          </p>
        </div>
      ),
    },
    {
      key: 'beneficiary',
      header: 'Beneficiary',
      render: (p) => (
        <div className="min-w-0">
          <p className="truncate text-xs text-content">{p.beneficiary?.name ?? '—'}</p>
          <p className="numeral mt-1 truncate text-2xs text-content-faint">
            {p.beneficiary?.vpa ?? (p.beneficiary?.last4 ? `••••${p.beneficiary.last4} · ${p.beneficiary.ifsc}` : '')}
          </p>
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (p) => <StatusPill status={p.status} /> },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (p) => <Money amount={p.amount.amount} currency={p.amount.currency} className="text-xs" />,
    },
    {
      key: 'total',
      header: 'Debited',
      align: 'right',
      render: (p) => (
        <Money amount={p.total_debited.amount} currency={p.total_debited.currency} className="text-xs text-content-subtle" />
      ),
    },
    {
      key: 'utr',
      header: 'UTR',
      render: (p) => (
        <span className="numeral text-2xs text-content-subtle">
          {p.utr ?? (p.failure_reason ? <span className="text-danger-text">{p.failure_reason}</span> : '—')}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'Created',
      align: 'right',
      render: (p) => <span className="text-xs text-content-subtle">{relativeTime(p.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Payouts</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
            Transfers out of your balance — to vendors, sellers or your own accounts. Everything created through the
            API appears here too. The amount plus its fee is held from your available balance the moment a payout is
            queued.
          </p>
          {inFlight > 0 && (
            <p className="mt-2 text-xs text-content-subtle">
              <span className="numeral text-content">{inFlight}</span> awaiting bank confirmation
            </p>
          )}
        </div>
        {!creating && (
          <button type="button" onClick={() => { setCreating(true); setError(null); }} className="btn btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New payout
          </button>
        )}
      </div>

      {creating && (
        <CreatePayout
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['payouts'] });
          }}
        />
      )}

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        loading={payouts.isLoading}
        empty={
          <EmptyState
            title="No payouts yet"
            description="Create one here, or POST /v1/payouts from your server. Both land in this list."
          />
        }
      />

      <p className="flex items-start gap-2 border-t border-line pt-4 text-2xs leading-relaxed text-content-faint">
        <ArrowUpRight className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        A payout that the bank rejects is reversed and the full amount, including its fee, returns to your available
        balance.
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Create
 * ------------------------------------------------------------------------ */

function CreatePayout({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [rail, setRail] = useState<'IMPS' | 'NEFT' | 'RTGS' | 'UPI'>('IMPS');
  const [form, setForm] = useState({
    amount: '',
    purpose: 'VENDOR_PAYMENT',
    name: '',
    account_number: '',
    ifsc: '',
    vpa: '',
    reference: '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const isUpi = rail === 'UPI';

  const create = useMutation({
    mutationFn: () =>
      api.post('/payouts', {
        amount: form.amount.trim(),
        purpose: form.purpose,
        rail,
        reference: form.reference.trim() || undefined,
        beneficiary: {
          name: form.name.trim(),
          ...(isUpi
            ? { vpa: form.vpa.trim() }
            : { account_number: form.account_number.trim(), ifsc: form.ifsc.trim().toUpperCase() }),
        },
      }),
    onSuccess: onCreated,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not create the payout.'),
  });

  const ready =
    form.amount.trim().length > 0 &&
    form.name.trim().length > 1 &&
    (isUpi ? form.vpa.includes('@') : form.account_number.trim().length >= 9 && form.ifsc.trim().length === 11);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
      className="rounded-lg border border-line bg-surface-raised p-5"
    >
      <div className="mb-5 flex items-start justify-between gap-4 border-b border-line pb-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md border border-line bg-surface-inset">
            <Banknote className="h-3.5 w-3.5 text-content-subtle" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-sm font-medium text-content">New payout</h3>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-content-subtle">
              The amount and the payout fee are debited from your available balance immediately. A rejected transfer
              is reversed in full.
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost h-7 w-7 p-0" aria-label="Cancel">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="po-amount" className="label">
            Amount (₹)
          </label>
          <input id="po-amount" required inputMode="decimal" value={form.amount} onChange={set('amount')} placeholder="25000.00" className="input numeral" />
        </div>

        <div>
          <label htmlFor="po-purpose" className="label">
            Purpose
          </label>
          <select id="po-purpose" value={form.purpose} onChange={set('purpose')} className="input">
            {PURPOSES.map((v) => (
              <option key={v} value={v}>
                {enumLabel(v)}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <span className="label">Rail</span>
          <div className="flex flex-wrap gap-2">
            {(['IMPS', 'NEFT', 'RTGS', 'UPI'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRail(value)}
                aria-pressed={rail === value}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  rail === value
                    ? 'border-brand-700 bg-brand-soft text-brand-300'
                    : 'border-line text-content-subtle hover:text-content'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-2xs text-content-faint">
            IMPS and UPI settle within minutes. NEFT and RTGS follow banking hours.
          </p>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="po-name" className="label">
            Beneficiary name
          </label>
          <input id="po-name" required value={form.name} onChange={set('name')} className="input" placeholder="As registered with their bank" />
        </div>

        {isUpi ? (
          <div className="sm:col-span-2">
            <label htmlFor="po-vpa" className="label">
              Beneficiary VPA
            </label>
            <input id="po-vpa" required value={form.vpa} onChange={set('vpa')} className="input numeral" placeholder="name@bank" />
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="po-account" className="label">
                Account number
              </label>
              <input id="po-account" required inputMode="numeric" value={form.account_number} onChange={set('account_number')} className="input numeral" />
            </div>
            <div>
              <label htmlFor="po-ifsc" className="label">
                IFSC
              </label>
              <input id="po-ifsc" required maxLength={11} value={form.ifsc} onChange={set('ifsc')} className="input numeral uppercase" />
            </div>
          </>
        )}

        <div className="sm:col-span-2">
          <label htmlFor="po-ref" className="label">
            Your reference (optional)
          </label>
          <input id="po-ref" value={form.reference} onChange={set('reference')} className="input" placeholder="INV-2291" />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={create.isPending || !ready}>
          {create.isPending ? <Spinner /> : <Send className="h-4 w-4" aria-hidden="true" />}
          Queue payout
        </button>
      </div>
    </form>
  );
}
